import { gql, useLazyQuery } from '@apollo/client';
import { SemanticUI } from '@iterable/deprecated_react_components';
import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { getErrorStub, validateNumberRange } from 'modules/workflowNodeEditors/utils';
import { InputText } from 'ui/inputs';
import { Banner } from 'ui/banner';
import { BannerVariants } from 'ui/banner/types';
import {
  DatePickerInput,
  DatePickerWithCollapsibleCalendar,
  TimeInput,
  TimeZonePicker,
} from 'ui/datePicker';
import { FormField } from 'ui/formField';
import { ALIGNMENT } from 'ui/selects';
import { Spinner } from 'ui/spinner';
import { colors, spacing } from 'ui/theme';
import { HelpToggletip } from 'ui/tooltip';
import { pxToRem } from 'utils/convert';
import noop from 'utils/noop';

import {
  FormHeadingText,
  FormHeadingWrapper,
  FormSectionContent,
  RowWrapper,
} from '../../../EditorBody.styled';
import { SkipDaysOfWeekForm } from '../../formElements';
import { RECUR_DAYS_RANGE } from '../../StartNodeEditor/ScheduleEntrySource/Scheduler/Scheduler.constants';
// import { SRLNodeData } from '../../StartNodeEditor/ScheduleEntrySource/Scheduler/Scheduler.types';
import {
  epochToLocalMoment,
  getMinutesFromMidnight,
  getSRLDefaultStateValues,
  getSRLNodeDataValues,
  getSRLStateValues,
  parseEndDateForSaving,
  parseStartDateForSaving,
} from '../../StartNodeEditor/ScheduleEntrySource/Scheduler/Scheduler.utils';

export const RECUR_DAYS_TEST_ID = 'recur-days';

const getProjectTimezoneQuery = gql`
  query getProjectTimezone {
    currentProject {
      timeZone
    }
  }
`;

const StyledInputText = styled(InputText)`
  width: ${pxToRem(110)};
  input {
    width: ${spacing.base};
  }
`;

const ErrorText = styled.div`
  color: ${colors.negative100};
`;

export const SCHEDULED_RECURRING_LIST_TRIGGER_ID = 'scheduled-recurring-list-trigger';
export const START_TIME_ID = 'start-time';
export const RECURRENCE_FREQUENCY_ID = 'recurrence-frequency';
export const END_BEFORE_START_WARNING_TEST_ID = 'end-before-start-warning';

const content = {
  infoBanner:
    'The recurring trigger is scheduled when you enable the workflow or save the workflow if it is already enabled. Disable the workflow to cancel.',
  endDateToggleTip:
    'If set, the End time will be at 11:59 PM in the same timezone set for the Start date',
};

const Wrapper = (props) => <div data-test={SCHEDULED_RECURRING_LIST_TRIGGER_ID} {...props} />;

// type Props = {
//   /** Callback that updates currently tracked trigger data */
//   onChange: (nodeData: Partial<StartNodeData>) => void,
//   /** Subset of node data specifically used for scheduled recurring list trigger */
//   nodeData: StartNodeData | SRLNodeData,
//   /** is tile editor read only */
//   isReadOnly?: boolean,
// };

/**
 * NOTE: No longer used after StartTileRefresh is GA
 */
export const ScheduledRecurringListTrigger = (
  { onChange = noop, nodeData = {}, isReadOnly } /*: Props */,
) => {
  const { recurDays, skipDaysOfWeek, timeZone } = nodeData;
  const [recurDaysError, setRecurDaysError] = useState(getErrorStub());
  const [isEndDateValid, setIsEndDateValid] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [minStart, setMinStart] = useState(moment());
  const [momentStart, setMomentStart] = useState(moment());
  const [momentStartTime, setMomentStartTime] = useState(moment());
  const [momentEnd, setMomentEnd] = useState(null);

  const updateInitialValues = (data) => {
    setMinStart(data.minStart);
    setMomentStart(data.momentStart);
    setMomentStartTime(data.momentStartTime);
    setMomentEnd(data.momentEnd);
  };

  const [getProjectTimeZone] = useLazyQuery(getProjectTimezoneQuery, {
    fetchPolicy: 'cache-and-network',
    onCompleted: (resp) => {
      const data = getSRLDefaultStateValues(resp?.currentProject?.timeZone);
      onChange(getSRLNodeDataValues(data));
      updateInitialValues(data);
      setIsLoading(false);
    },
  });

  useEffect(() => {
    setRecurDaysError(validateNumberRange(recurDays, RECUR_DAYS_RANGE));
  }, [recurDays]);

  useEffect(() => {
    if (timeZone) {
      const data = getSRLStateValues(nodeData);
      updateInitialValues(data);
      setIsLoading(false);
    } else {
      getProjectTimeZone();
    }
  }, []);

  useEffect(() => {
    // check for validity on start or end date change
    if (momentEnd && momentStart > momentEnd) {
      setIsEndDateValid(false);
    } else {
      setIsEndDateValid(true);
    }
  }, [momentStart, momentEnd]);

  if (!timeZone || isLoading) {
    return (
      <Wrapper>
        <Spinner />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <FormHeadingWrapper>
        <Banner variant={BannerVariants.INFO}>{content.infoBanner}</Banner>
      </FormHeadingWrapper>
      <FormHeadingWrapper>
        <FormHeadingText>Start date and time</FormHeadingText>
      </FormHeadingWrapper>
      <FormSectionContent>
        <RowWrapper>
          <DatePickerWithCollapsibleCalendar
            date={momentStart}
            onDateChange={(date) => {
              onChange({ startDate: parseStartDateForSaving(date, timeZone) });
              setMomentStart(date);
            }}
            validDateRange={{ start: minStart }}
            isDisabled={isReadOnly}
            isReadOnly={isReadOnly}
          />
          <DatePickerInput>
            <TimeInput
              id={START_TIME_ID}
              onTimeChange={(date) => {
                setMomentStartTime(date);
                onChange({ minuteOfDay: getMinutesFromMidnight(date) });
              }}
              time={momentStartTime}
              isReadOnly={isReadOnly}
              isDisabled={isReadOnly}
            />
          </DatePickerInput>
          <TimeZonePicker
            horizontalAlignment={ALIGNMENT.RIGHT}
            onTimeZoneChange={([tz]) => {
              setMinStart(epochToLocalMoment(moment().valueOf(), tz));
              onChange({ timeZone: tz });
            }}
            timeZone={timeZone}
            isDisabled={isReadOnly}
          />
        </RowWrapper>
      </FormSectionContent>
      <FormHeadingWrapper>
        <FormHeadingText>End date</FormHeadingText>
      </FormHeadingWrapper>
      <FormSectionContent>
        <RowWrapper>
          <DatePickerWithCollapsibleCalendar
            canClearDate
            date={momentEnd}
            onDateChange={(date) => {
              onChange({ endDate: parseEndDateForSaving(date, timeZone) });
              setMomentEnd(date);
            }}
            placeholder="Optional"
            validDateRange={{ start: momentStartTime }}
            isDisabled={isReadOnly}
          />
          <HelpToggletip tooltipContent={content.endDateToggleTip} />
        </RowWrapper>
        {!isEndDateValid && (
          <ErrorText data-test={END_BEFORE_START_WARNING_TEST_ID}>
            <SemanticUI.Icon name="warning sign" />
            The end date cannot be before the start date.
          </ErrorText>
        )}
      </FormSectionContent>
      <FormHeadingWrapper>
        <FormHeadingText>Run every</FormHeadingText>
      </FormHeadingWrapper>
      <FormSectionContent>
        <RowWrapper>
          <FormField
            errorText={recurDaysError.message}
            hasError={recurDaysError.hasError}
            inputId={RECURRENCE_FREQUENCY_ID}
          >
            <StyledInputText
              id={RECURRENCE_FREQUENCY_ID}
              onBlur={() => {
                const numericValue = Number(recurDays);
                if (numericValue < RECUR_DAYS_RANGE.min || Number.isNaN(numericValue)) {
                  // If the user navigates away when the field is blank, set the
                  // value to the minimum
                  onChange({ recurDays: RECUR_DAYS_RANGE.min });
                } else if (numericValue > RECUR_DAYS_RANGE.max) {
                  // If the user navigates away when the field is too large, set the
                  // value to the maximum
                  onChange({ recurDays: RECUR_DAYS_RANGE.max });
                }
              }}
              onChange={(value) => {
                // Allow the user to blank out the field so that they can easily
                // type a number that does not begin with 1
                onChange({ recurDays: value === '' ? null : Number(value) });
              }}
              Suffix={<div>{Number(recurDays) === 1 ? 'day' : 'days'}</div>}
              type="number"
              value={recurDays?.toString()}
              isDisabled={isReadOnly}
              min={RECUR_DAYS_RANGE.min}
              max={RECUR_DAYS_RANGE.max}
              data-test={RECUR_DAYS_TEST_ID}
            />
          </FormField>
        </RowWrapper>
      </FormSectionContent>
      <FormHeadingWrapper>
        <FormHeadingText>Send skips on...</FormHeadingText>
      </FormHeadingWrapper>
      <FormSectionContent>
        <SkipDaysOfWeekForm
          skipDaysOfWeek={skipDaysOfWeek}
          onSkipDaysOfWeekUpdate={(value) => onChange({ skipDaysOfWeek: value })}
          isReadOnly={isReadOnly}
        />
      </FormSectionContent>
    </Wrapper>
  );
};
